/**
 * Shell-wide UI preference store: theme (light/dark) + density mode.
 *
 * Previously theme and density were written to localStorage from three
 * independent call sites (TopBar toggle, CommandPalette toggles, AppShell
 * restore-on-mount) with no cross-tab sync — a control-room workstation with
 * two Tyndo tabs could disagree about dark mode until one was reloaded.
 *
 * This module is the single owner of both preferences:
 *   - applies them to <html> (`.dark` class / `data-density` attribute),
 *   - persists to the existing localStorage keys (`tyndo-theme`,
 *     `tyndo-density`) so older sessions read the same values,
 *   - syncs across tabs via BroadcastChannel, with the `storage` event as the
 *     fallback path for browsers without BroadcastChannel,
 *   - exposes a `useSyncExternalStore`-backed hook for chrome components.
 */

import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark';
export type DensityPreference = 'comfortable' | 'compact' | 'touch' | 'wall';

export const DENSITY_OPTIONS: readonly DensityPreference[] = [
  'comfortable',
  'compact',
  'touch',
  'wall',
];

export interface UiPreferences {
  theme: ThemePreference;
  density: DensityPreference;
}

const THEME_KEY = 'tyndo-theme';
const DENSITY_KEY = 'tyndo-density';
const CHANNEL_NAME = 'tyndo.ui-prefs';

function readStoredTheme(): ThemePreference {
  try {
    if (window.localStorage.getItem(THEME_KEY) === 'dark') return 'dark';
  } catch {
    /* storage unavailable — fall through to the DOM class set pre-paint */
  }
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) {
    return 'dark';
  }
  return 'light';
}

function readStoredDensity(): DensityPreference {
  try {
    const stored = window.localStorage.getItem(DENSITY_KEY);
    if (stored && (DENSITY_OPTIONS as readonly string[]).includes(stored)) {
      return stored as DensityPreference;
    }
  } catch {
    /* ignore */
  }
  return 'comfortable';
}

let current: UiPreferences = { theme: 'light', density: 'comfortable' };
let initialized = false;
const listeners = new Set<() => void>();
let channel: BroadcastChannel | null = null;

function applyToDocument(prefs: UiPreferences): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', prefs.theme === 'dark');
  if (prefs.density === 'comfortable') {
    document.documentElement.removeAttribute('data-density');
  } else {
    document.documentElement.setAttribute('data-density', prefs.density);
  }
}

function notify(): void {
  for (const fn of Array.from(listeners)) {
    try {
      fn();
    } catch (err) {
      console.error('uiPreferences listener threw', err);
    }
  }
}

/** Adopt a preference change that originated in ANOTHER tab (no re-broadcast). */
function adoptRemote(partial: Partial<UiPreferences>): void {
  const next = { ...current, ...partial };
  if (next.theme === current.theme && next.density === current.density) return;
  current = next;
  applyToDocument(current);
  notify();
}

/**
 * Idempotent bootstrap: read persisted values, apply them to <html>, and wire
 * the cross-tab listeners. Called once from AppShell's mount effect; safe to
 * call again (e.g. from Storybook decorators).
 */
export function initUiPreferences(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  current = { theme: readStoredTheme(), density: readStoredDensity() };
  applyToDocument(current);

  // Primary sync path: BroadcastChannel (works even when storage writes are
  // blocked, e.g. private mode where the in-memory preference still matters).
  if (typeof BroadcastChannel === 'function') {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent) => {
        const data = event.data as Partial<UiPreferences> | undefined;
        if (!data || typeof data !== 'object') return;
        const partial: Partial<UiPreferences> = {};
        if (data.theme === 'light' || data.theme === 'dark') partial.theme = data.theme;
        if (
          typeof data.density === 'string' &&
          (DENSITY_OPTIONS as readonly string[]).includes(data.density)
        ) {
          partial.density = data.density as DensityPreference;
        }
        adoptRemote(partial);
      };
    } catch {
      channel = null;
    }
  }

  // Fallback sync path: the `storage` event fires in OTHER tabs on every
  // localStorage write, so even without BroadcastChannel a toggle propagates.
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === THEME_KEY) {
      adoptRemote({ theme: event.newValue === 'dark' ? 'dark' : 'light' });
    } else if (event.key === DENSITY_KEY) {
      const value = event.newValue ?? 'comfortable';
      if ((DENSITY_OPTIONS as readonly string[]).includes(value)) {
        adoptRemote({ density: value as DensityPreference });
      }
    }
  });
}

function persistAndBroadcast(partial: Partial<UiPreferences>): void {
  try {
    if (partial.theme) window.localStorage.setItem(THEME_KEY, partial.theme);
    if (partial.density) window.localStorage.setItem(DENSITY_KEY, partial.density);
  } catch {
    /* storage unavailable — in-memory + broadcast still work */
  }
  try {
    channel?.postMessage(partial);
  } catch {
    /* channel closed — storage event remains the sync path */
  }
}

export function getUiPreferences(): UiPreferences {
  return current;
}

export function setTheme(theme: ThemePreference): void {
  initUiPreferences();
  if (current.theme === theme) return;
  current = { ...current, theme };
  applyToDocument(current);
  persistAndBroadcast({ theme });
  notify();
}

export function toggleTheme(): void {
  setTheme(current.theme === 'dark' ? 'light' : 'dark');
}

export function setDensity(density: DensityPreference): void {
  initUiPreferences();
  if (current.density === density) return;
  current = { ...current, density };
  applyToDocument(current);
  persistAndBroadcast({ density });
  notify();
}

export function subscribeUiPreferences(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reactive view of the preferences for chrome components. */
export function useUiPreferences(): UiPreferences {
  return useSyncExternalStore(subscribeUiPreferences, getUiPreferences, getUiPreferences);
}
